import { Component } from './component.js';
import { ChapterPanel } from './chapter-panel.js';

/* --header-height and --top-bar-height are written as px today; read px or
   rem (16px root baseline) so a future stylesheet change cannot silently zero
   the measurement. Empty or unparsable input falls back to the supplied
   default, and a parsed 0 keeps the previous falsy-fallback behaviour. */
function readCssPx(raw, fallback) {
  const text = (raw || '').trim();
  let value = parseFloat(text);
  if (/rem$/.test(text)) value *= 16;
  return value || fallback;
}

/**
 * <product-sticky-bar> — the product page chapter bar: product name on the
 * left, one tab per chapter module on the right, and a Buy button that returns
 * to the purchase area at the top of the page. chrome.js flips the top chrome
 * for it once the bar has travelled up to the top of the viewport.
 *
 * Tabs are generated from the page itself: any module carrying
 * `data-sticky-anchor` + `data-tab-label` (product-story / product-in-the-box /
 * product-comparison / product-faq) gets one, so the set adapts per product
 * with no per-product configuration. The same scan fills the mobile chapter
 * panel, so the two lists can never drift apart.
 */
class ProductStickyBar extends Component {
  /* Private fields must be declared before use: without this the whole module
     fails to parse (SyntaxError) and the element is never upgraded, so no tabs
     are ever built. */
  #offset = 0;
  #chapters = null;

  connectedCallback() {
    super.connectedCallback();

    this.#chapters = new ChapterPanel(this, {
      panel: '[data-chapters-panel]',
      toggle: '.product-subnav__chapters-toggle',
      stage: '.product-subnav__chapters-stage',
      curtain: '[data-chapters-curtain]',
    });
    this.#chapters.connect();

    this.#buildTabs();
    this.#wireBuyButton();
  }

  disconnectedCallback() {
    this.#chapters?.disconnect();
    this.#chapters = null;
    super.disconnectedCallback();
  }

  /** Delegated from the chapter picker button (on:click). */
  toggleChapters() {
    this.#chapters?.toggle();
  }

  #buildTabs() {
    const inner = this.querySelector('.product-sticky-tabs__inner');
    const chapterStage = this.querySelector('.product-subnav__chapters-stage');
    if (!inner) {
      return;
    }

    // Only modules that rendered content on this page expose a data-tab-label,
    // so the tab set automatically adapts per product.
    const anchors = [...document.querySelectorAll('[data-sticky-anchor]')]
      .filter((el) => el.getAttribute('data-tab-label'));

    if (anchors.length === 0) {
      this.hidden = true;
      return;
    }

    const tabs = anchors.map((anchor, index) => {
      const label = anchor.getAttribute('data-tab-label');

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'product-sticky-tabs__tab' + (index === 0 ? ' active' : '');
      tab.textContent = label;
      tab.addEventListener('click', () => this.#scrollTo(anchor));
      inner.appendChild(tab);

      if (chapterStage) {
        const chapter = document.createElement('a');
        chapter.className = 'product-subnav__chapter-link';
        chapter.href = '#' + anchor.getAttribute('data-sticky-anchor');
        chapter.textContent = label;
        chapter.addEventListener('click', (event) => {
          event.preventDefault();
          this.#scrollTo(anchor);
          this.#chapters?.close();
        });
        chapterStage.appendChild(chapter);
      }

      return { tab, anchor };
    });

    const cs = getComputedStyle(document.documentElement);
    const headerHeight = readCssPx(cs.getPropertyValue('--header-height'), 64);
    const topBarHeight = readCssPx(cs.getPropertyValue('--top-bar-height'), 0);
    const barHeight = this.getBoundingClientRect().height || 0;
    this.#offset = headerHeight + topBarHeight + barHeight + 8;

    const rootMargin = `-${this.#offset}px 0px -60% 0px`;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const match = tabs.find((item) => item.anchor === entry.target);
          if (match) {
            this.#activate(match.tab);
          }
        }
      });
    }, { rootMargin, threshold: 0 });

    anchors.forEach((anchor) => observer.observe(anchor));
  }

  #wireBuyButton() {
    const button = this.querySelector('[data-ref="buyButton"]');
    if (!button) return;

    button.addEventListener('click', (event) => {
      /* The href stays as the no-JS fallback, but the browser's own hash jump
         would land instantly and beat the animation, so the scroll is driven
         here instead. */
      event.preventDefault();
      this.#scrollToTop();
    });
  }

  #activate(tab) {
    const all = this.querySelectorAll('.product-sticky-tabs__tab');
    all.forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');
  }

  /** Buy button: back to the purchase area, which sits at the top of the page. */
  #scrollToTop() {
    window.scrollTo({ top: 0, behavior: this.#scrollBehavior() });
  }

  #scrollTo(anchor) {
    const top = anchor.getBoundingClientRect().top + window.pageYOffset - (this.#offset || 0);
    window.scrollTo({ top, behavior: this.#scrollBehavior() });
  }

  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

if (!customElements.get('product-sticky-bar')) {
  customElements.define('product-sticky-bar', ProductStickyBar);
}
